/**
 * Coaches Report Page
 * 
 * Admin report showing all active coaches with filtering and export capabilities
 * - Filter by level and group
 * - Summary statistics
 * - Sortable table
 * - Export to CSV
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient, Rider, Group } from '../../api/rest-client';
import { Button } from '../../components/common/Button';
import { useGroupLeaderStore } from '../../store/groupLeaderStore';

interface CoachData {
  rider_id: string;
  name: string;
  level: string;
  group: string;
}

const CoachesReport: React.FC = () => {
  const navigate = useNavigate();
  const isGroupLeader = useGroupLeaderStore(s => s.isGroupLeader);
  const [coaches, setCoaches] = useState<CoachData[]>([]);
  const [filteredCoaches, setFilteredCoaches] = useState<CoachData[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [seasonFilter, setSeasonFilter] = useState<string>('');

  // Filters
  const [levelFilter, setLevelFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');

  // Sort state
  const [sortColumn, setSortColumn] = useState<'name' | 'level' | 'group'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadSeasonConfig();
  }, []);

  // Reload data when season filter changes
  useEffect(() => {
    if (seasonFilter) {
      loadData();
    }
  }, [seasonFilter]);

  useEffect(() => {
    applyFilters();
  }, [coaches, seasonFilter, levelFilter, groupFilter]);

  const loadSeasonConfig = async () => {
    try {
      const seasonConfig = await restClient.getSeasonConfig();
      setAvailableSeasons(seasonConfig.availableSeasons);
      setSeasonFilter(seasonConfig.currentSeason);
    } catch (err) {
      setError('Failed to load season configuration');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all riders (like legacy app)
      const allRiders = await restClient.getRiders();
      
      // Filter for coaches only (matching legacy app logic exactly)
      // Legacy checks: type === 'Coach' && disabled !== '1' && disabled !== 1
      // Filter by selected season - check both primary season and seasons_active array (OR logic)
      const coachesData = allRiders.filter(
        (rider: Rider) => {
          const isCoach = rider.type === 'Coach';
          const isNotDisabled = rider.disabled !== '1' && rider.disabled !== 1;
          // Check if rider is active in selected season
          // Either primary season matches OR seasons_active contains selected season
          const isSelectedSeason = rider.season === seasonFilter || 
            (rider.seasons_active && rider.seasons_active.includes(seasonFilter));
          
          return isCoach && isNotDisabled && isSelectedSeason;
        }
      );

      // Transform to coach data format
      const transformedCoaches: CoachData[] = coachesData.map((coach: Rider) => ({
        rider_id: coach.rider_id,
        name: coach.name,
        level: coach.level || '',
        group: coach.group || '',
      }));

      // Sort by name initially
      transformedCoaches.sort((a, b) => a.name.localeCompare(b.name));

      setCoaches(transformedCoaches);

      // Load groups for filter
      const groupsData = await restClient.getGroups();
      setGroups(groupsData);
    } catch (err) {
      setError('Failed to load coaches data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...coaches];

    // Level filter
    if (levelFilter) {
      filtered = filtered.filter((coach) => coach.level === levelFilter);
    }

    // Group filter
    if (groupFilter) {
      filtered = filtered.filter((coach) => coach.group === groupFilter);
    }

    setFilteredCoaches(filtered);
  };

  const clearFilters = useCallback(() => {
    setLevelFilter('');
    setGroupFilter('');
    // Don't reset season filter - keep it at current selection
  }, []);

  const handleSort = useCallback((column: 'name' | 'level' | 'group') => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);

    const sorted = [...filteredCoaches].sort((a, b) => {
      const aVal = a[column] || '';
      const bVal = b[column] || '';

      // For level, treat as numbers if possible
      if (column === 'level') {
        const aNum = parseInt(aVal);
        const bNum = parseInt(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return newDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      return newDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    setFilteredCoaches(sorted);
  }, [sortColumn, sortDirection, filteredCoaches]);

  const exportToCSV = useCallback(() => {
    if (filteredCoaches.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Name', 'Level', 'Group'];
    const csvContent = [
      headers.join(','),
      ...filteredCoaches.map((coach) =>
        [
          `"${coach.name}"`,
          `"${coach.level}"`,
          `"${coach.group}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `coaches-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredCoaches]);

  // Calculate statistics
  const getStatistics = () => {
    const groupCounts: Record<string, number> = {};
    const levelCounts: Record<string, number> = {};

    filteredCoaches.forEach((coach) => {
      const group = coach.group || 'No Group';
      groupCounts[group] = (groupCounts[group] || 0) + 1;

      const validLevels = ['None', '1', '2', '3'];
      let level = coach.level || 'None';
      if (!validLevels.includes(level)) {
        level = 'None';
      }
      levelCounts[level] = (levelCounts[level] || 0) + 1;
    });

    return { groupCounts, levelCounts };
  };

  const { groupCounts, levelCounts } = getStatistics();

  // Get unique levels for filter
  const uniqueLevels = [...new Set(coaches.map((c) => c.level).filter((l) => l))].sort((a, b) => parseInt(b) - parseInt(a));

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading coaches data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Coaches Report</h1>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/admin/reports')}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Reports
            </Button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">Report Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <strong>Total Coaches:</strong> {coaches.length}
            </div>
            <div>
              <strong>Filtered Results:</strong> {filteredCoaches.length}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <strong>Coaches by Group:</strong>
              <div className="text-sm mt-1">
                {Object.entries(groupCounts).length > 0 ? (
                  Object.entries(groupCounts).map(([group, count]) => (
                    <div key={group}>
                      {group}: {count}
                    </div>
                  ))
                ) : (
                  <div>None</div>
                )}
              </div>
            </div>
            <div>
              <strong>Coaches by Level:</strong>
              <div className="text-sm mt-1">
                {Object.entries(levelCounts)
                  .sort(([a], [b]) => {
                    if (a === 'None') return 1;
                    if (b === 'None') return -1;
                    return parseInt(b) - parseInt(a);
                  })
                  .map(([level, count]) => (
                    <div key={level}>
                      Level {level}: {count}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block font-semibold mb-2">Filter by Season:</label>
              <select
                value={seasonFilter}
                onChange={(e) => setSeasonFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {availableSeasons.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Filter by Level:</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">All Levels</option>
                {uniqueLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Filter by Group:</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">All Groups</option>
                {groups.map((group) => (
                  <option key={group.group_id} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2">Actions:</label>
              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('name')}
                >
                  Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('level')}
                >
                  Level {sortColumn === 'level' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="border border-gray-300 px-4 py-2 text-left cursor-pointer hover:bg-gray-200"
                  onClick={() => handleSort('group')}
                >
                  Group {sortColumn === 'group' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCoaches.length === 0 ? (
                <tr>
                  <td colSpan={3} className="border border-gray-300 px-4 py-2 text-center">
                    No coaches found
                  </td>
                </tr>
              ) : (
                filteredCoaches.map((coach, index) => {
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">{coach.name}{isGroupLeader(coach.rider_id) ? ' ⭐' : ''}</td>
                      <td className="border border-gray-300 px-4 py-2">
                        {coach.level || ''}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">{coach.group}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CoachesReport;
